// Per-folder export to Anki's .apkg format. Mirrors ImportModal.jsx's Anki importer in
// reverse: that code only ever reads the `notes` table's `flds` column, so this writer's
// job is to produce a minimal, valid Anki 2.1 SQLite collection that a real Anki client
// (and our own importer) can open. Text only for v1 — card images are intentionally not
// carried over; embedding them would require rewriting field HTML with <img> tags plus a
// populated media manifest, real added complexity not worth it for a first pass.

import { renderContentForPdf } from './pdf.js';
import { safeImport } from './safeImport.js';

// Must match the installed sql.js package version (frontend/package.json) — the WASM
// loader requests version-specific filenames (e.g. sql-wasm-browser.wasm didn't exist
// yet in 1.12.0), so a mismatched CDN pin 404s and sql.js aborts entirely.
const SQLJS_LOCATE_FILE = (file) => `https://cdnjs.cloudflare.com/ajax/libs/sql.js/1.14.1/${file}`;

function prepareField(content) {
    const sanitized = renderContentForPdf(content ?? '');
    const escaped = String(sanitized)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    return escaped.replace(/\n/g, '<br>');
}

async function fieldChecksum(text) {
    const bytes = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest('SHA-1', bytes);
    const hex = Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
    return parseInt(hex.slice(0, 8), 16);
}

function randomGuid() {
    return Math.random().toString(36).slice(2, 12);
}

const SCHEMA_STATEMENTS = [
    `CREATE TABLE col (
        id integer primary key, crt integer not null, mod integer not null, scm integer not null,
        ver integer not null, dty integer not null, usn integer not null, ls integer not null,
        conf text not null, models text not null, decks text not null, dconf text not null, tags text not null
    )`,
    `CREATE TABLE notes (
        id integer primary key, guid text not null, mid integer not null, mod integer not null,
        usn integer not null, tags text not null, flds text not null, sfld text not null,
        csum integer not null, flags integer not null, data text not null
    )`,
    `CREATE TABLE cards (
        id integer primary key, nid integer not null, did integer not null, ord integer not null,
        mod integer not null, usn integer not null, type integer not null, queue integer not null,
        due integer not null, ivl integer not null, factor integer not null, reps integer not null,
        lapses integer not null, left integer not null, odue integer not null, odid integer not null,
        flags integer not null, data text not null
    )`,
    `CREATE TABLE revlog (
        id integer primary key, cid integer not null, usn integer not null, ease integer not null,
        ivl integer not null, lastIvl integer not null, factor integer not null, time integer not null,
        type integer not null
    )`,
    `CREATE TABLE graves (usn integer not null, oid integer not null, type integer not null)`,
    `CREATE INDEX ix_notes_usn on notes (usn)`,
    `CREATE INDEX ix_cards_usn on cards (usn)`,
    `CREATE INDEX ix_revlog_usn on revlog (usn)`,
    `CREATE INDEX ix_cards_nid on cards (nid)`,
    `CREATE INDEX ix_cards_sched on cards (did, queue, due)`,
    `CREATE INDEX ix_revlog_cid on revlog (cid)`,
    `CREATE INDEX ix_notes_csum on notes (csum)`,
];

export async function exportFolderToAnki(folder) {
    const cards = (folder.cards || []).filter((c) => c.question || c.answer);
    if (cards.length === 0) throw new Error('This folder has no cards to export.');

    const [JSZipModule, initSqlJsModule] = await Promise.all([
        safeImport(() => import('jszip'), 'Anki export'),
        safeImport(() => import('sql.js'), 'Anki export'),
    ]);
    const JSZip = JSZipModule.default;
    const initSqlJs = initSqlJsModule.default;
    const SQL = await initSqlJs({ locateFile: SQLJS_LOCATE_FILE });

    const db = new SQL.Database();
    try {
        SCHEMA_STATEMENTS.forEach((sql) => db.run(sql));

        const now = Date.now();
        const nowSec = Math.floor(now / 1000);
        const modelId = now;
        const deckId = now + 1;

        const models = {
            [String(modelId)]: {
                id: modelId, name: 'Basic', type: 0, mod: nowSec, usn: -1, sortf: 0, did: deckId,
                tmpls: [{ name: 'Card 1', ord: 0, qfmt: '{{Front}}', afmt: '{{FrontSide}}<hr id="answer">{{Back}}', did: null, bqfmt: '', bafmt: '' }],
                flds: [
                    { name: 'Front', ord: 0, sticky: false, rtl: false, font: 'Arial', size: 20 },
                    { name: 'Back', ord: 1, sticky: false, rtl: false, font: 'Arial', size: 20 },
                ],
                css: '.card { font-family: arial; font-size: 20px; text-align: center; color: black; background-color: white; }',
                latexPre: '\\documentclass[12pt]{article}\n\\special{papersize=3in,5in}\n\\usepackage[utf8]{inputenc}\n\\usepackage{amssymb,amsmath}\n\\pagestyle{empty}\n\\setlength{\\parindent}{0in}\n\\begin{document}\n',
                latexPost: '\\end{document}',
                req: [[0, 'any', [0]]],
            },
        };

        const decks = {
            [String(deckId)]: {
                id: deckId, name: folder.name || 'FlashFonic Deck', mod: nowSec, usn: -1,
                lrnToday: [0, 0], revToday: [0, 0], newToday: [0, 0], timeToday: [0, 0],
                collapsed: true, browserCollapsed: true, desc: '', dyn: 0, conf: 1, extendNew: 0, extendRev: 0,
            },
        };

        const dconf = {
            1: {
                id: 1, name: 'Default', mod: 0, usn: -1, maxTaken: 60, autoplay: true, timer: 0, replayq: true,
                new: { perDay: 20, delays: [1, 10], ints: [1, 4, 7], initialFactor: 2500, order: 1, bury: false },
                rev: { perDay: 200, ease4: 1.3, fuzz: 0.05, minSpace: 1, ivlFct: 1, maxIvl: 36500, bury: false },
                lapse: { delays: [10], mult: 0, minInt: 1, leechFails: 8, leechAction: 0 },
                dyn: false, newMix: 0, newPerDayMinimum: 0, interday: [],
            },
        };

        const conf = {
            nextPos: cards.length + 1, estTimes: true, activeDecks: [deckId], sortType: 'noteFld', timeLim: 0,
            sortBackwards: false, addToCur: true, curDeck: deckId, newBury: true, newSpread: 0,
            dueCounts: true, curModel: String(modelId), collapseTime: 1200,
        };

        db.run(
            `INSERT INTO col (id, crt, mod, scm, ver, dty, usn, ls, conf, models, decks, dconf, tags) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?)`,
            [1, nowSec, now, now, 11, 0, 0, 0, JSON.stringify(conf), JSON.stringify(models), JSON.stringify(decks), JSON.stringify(dconf), '{}']
        );

        let idCounter = now;
        for (let i = 0; i < cards.length; i += 1) {
            const card = cards[i];
            const noteId = idCounter++;
            const cardId = idCounter++;
            const front = prepareField(card.question);
            const back = prepareField(card.answer);
            const flds = `${front}\x1f${back}`;
            const csum = await fieldChecksum(front);

            db.run(
                `INSERT INTO notes (id, guid, mid, mod, usn, tags, flds, sfld, csum, flags, data) VALUES (?,?,?,?,?,?,?,?,?,?,?)`,
                [noteId, randomGuid(), modelId, nowSec, -1, '', flds, front, csum, 0, '']
            );
            db.run(
                `INSERT INTO cards (id, nid, did, ord, mod, usn, type, queue, due, ivl, factor, reps, lapses, left, odue, odid, flags, data) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
                [cardId, noteId, deckId, 0, nowSec, -1, 0, 0, i + 1, 0, 0, 0, 0, 0, 0, 0, 0, '']
            );
        }

        const collectionBytes = db.export();
        const zip = new JSZip();
        zip.file('collection.anki21', collectionBytes);
        zip.file('media', '{}');
        const blob = await zip.generateAsync({ type: 'blob' });

        const safeName = (folder.name || 'FlashFonic-deck').replace(/[\\/:*?"<>|]/g, '_');
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${safeName}.apkg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    } finally {
        db.close();
    }
}
